import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Statistic,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  Alert
} from 'antd';
import {
  ReloadOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  HddOutlined,
  DatabaseOutlined,
  FireOutlined
} from '@ant-design/icons';
import systemService from '../../services/systemService';

const { Title, Text } = Typography;

// 状态映射（后端返回 healthy/warning/critical）
const STATUS_MAP = {
  healthy: { color: 'success', text: '健康' },
  warning: { color: 'warning', text: '警告' },
  critical: { color: 'error', text: '严重' }
};

// 根据使用率给进度条上色
const usageColor = (value) => {
  if (value >= 95) return '#cf1322';
  if (value >= 80) return '#faad14';
  return '#3f8600';
};

// 将秒格式化为 x天 x小时 x分
const formatUptime = (seconds) => {
  if (!seconds || seconds <= 0) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d} 天`);
  if (h) parts.push(`${h} 小时`);
  parts.push(`${m} 分`);
  return parts.join(' ');
};

const SystemHealth = () => {
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemService.getHealth();
      if (res.success) {
        setHealth(res.data);
        setUpdatedAt(res.data?.timestamp ? new Date(res.data.timestamp * 1000) : new Date());
      }
    } catch (error) {
      // 错误已由拦截器提示，这里仅保底日志
      console.error('获取系统健康状态失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, 30000);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  const metrics = health?.metrics || {};
  const statusInfo = STATUS_MAP[health?.status] || { color: 'default', text: health?.status || '未知' };

  const usageCards = [
    { key: 'cpu_usage', title: 'CPU 使用率', icon: <FireOutlined /> },
    { key: 'memory_usage', title: '内存使用率', icon: <DatabaseOutlined /> },
    { key: 'disk_usage', title: '磁盘使用率', icon: <HddOutlined /> }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          <DashboardOutlined style={{ marginRight: 8 }} />
          系统监控
        </Title>
        <Space>
          {updatedAt && (
            <Text type="secondary">
              更新于 {updatedAt.toLocaleTimeString()}
            </Text>
          )}
          <Button icon={<ReloadOutlined />} onClick={fetchHealth} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      <Alert
        message="服务器实时监控"
        description="每 30 秒自动刷新一次。当任一指标超过 80% 显示警告，超过 95% 显示严重。"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Spin spinning={loading && !health}>
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>整体状态</Text>
                <Tag color={statusInfo.color} style={{ fontSize: 16, padding: '4px 12px' }}>
                  {statusInfo.text}
                </Tag>
              </div>
            </Col>
            <Col span={8}>
              <Statistic
                title="系统运行时长"
                value={formatUptime(metrics.uptime)}
                prefix={<ClockCircleOutlined />}
              />
            </Col>
          </Row>
        </Card>

        <Row gutter={16}>
          {usageCards.map((card) => {
            const value = Number(metrics[card.key] || 0);
            return (
              <Col span={8} key={card.key} style={{ marginBottom: 16 }}>
                <Card title={<span>{card.icon} {card.title}</span>}>
                  <Progress
                    type="dashboard"
                    percent={value}
                    strokeColor={usageColor(value)}
                    format={(p) => `${p}%`}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      </Spin>
    </div>
  );
};

export default SystemHealth;
